import { DOCUMENT } from '@angular/common';
import { DestroyRef, effect, inject, Injectable, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApplicationVersionService } from '@core/services/application-version.service';
import { ToastrService } from '@openng/ngx-toastr';
import { take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class ApplicationUpdateToastService {
  private readonly versionService = inject(ApplicationVersionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(ToastrService);

  private activeToast: ReturnType<ToastrService['info']> | null = null;
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;

    effect(
      () => {
        if (!this.versionService.updateAvailable() || this.activeToast) return;
        this.showUpdateToast();
      },
      { injector: this.injector },
    );
  }

  private showUpdateToast(): void {
    const href = escapeHtmlAttribute(this.document.location.href);

    const title = escapeHtmlAttribute(this.translateService.instant('CHS_NewerVersionAvailable'));
    const message = escapeHtmlAttribute(this.translateService.instant('CHS_ReloadForLatest'));
    const button = escapeHtmlAttribute(this.translateService.instant('CHS_ReloadPage'));

    const toast = this.toastService.info(
      `<p class="tds-detail-02">${message}</p><a href="${href}">${button}</a>`,
      escapeHtmlAttribute(title),
      {
        closeButton: true,
        disableTimeOut: true,
        enableHtml: true,
        tapToDismiss: false,
      },
    );

    this.activeToast = toast;

    toast.onHidden.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.activeToast = null;
      this.versionService.dismissUpdate();
    });
  }
}

function escapeHtmlAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}
